"use client"

import { useState, useEffect } from "react"
import { render } from "storyblok-rich-text-react-renderer"
import cn from "classnames"
import useSimpleModal from "@/utilities/useSimpleModal"
import Image from "@/components/Image"
import CallToAction from "@/components/CallToAction"
import Modal from "@/components/Modal"
import CopyIcon from "../../public/assets/copy-button.svg"
import ScreenshotIcon from "../../public/assets/screenshot-button.svg"

const PlatformCard = ({ platform, clientName, csmEmail, tikTokQR, pageUid }) => {
  const [copied, setCopied] = useState(false)
  const { open, openModal, closeModal } = useSimpleModal()
  const [status, setStatus] = useState("idle")
  const [submittedType, setSubmittedType] = useState(null)
  const storageKey = `platform-progress-${pageUid}-${platform.uuid}`
  const [activeStep, setActiveStep] = useState(null)

  useEffect(() => {
    if (typeof window === "undefined") return

    const saved = localStorage.getItem(storageKey)
    if (!saved) return

    try {
      const parsed = JSON.parse(saved)
      if (parsed?.status) {
        setSubmittedType(parsed.status)
        setStatus("success")
      }
    } catch (e) {
      console.error("Failed to parse platform progress", e)
    }
  }, [storageKey])

  const resetPlatform = () => {
    localStorage.removeItem(storageKey)
    setSubmittedType(null)
    setStatus("idle")
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(platform.content.access_info)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      console.error("Copy failed", e)
    }
  }

  const notifyPlatform = async (type) => {
    try {
      if (status === "sending") return

      setSubmittedType(type)
      setStatus("sending")

      const res = await fetch("/api/notify-platform", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type,
          clientName,
          platformName: platform.name,
          csmEmail,
        }),
      })

      if (!res.ok) throw new Error("Request failed")

      // ✅ Persist progress
      localStorage.setItem(storageKey, JSON.stringify({ status: type }))

      setStatus("success")
    } catch (err) {
      console.error(err)
      setStatus("error")
    }
  }


  const handleOpenScreenshot = (step) => {
    setActiveStep(step)
    openModal()
  }

  const handleCloseScreenshot = () => {
    closeModal()
    setActiveStep(null)
  }

  const isSending = status === "sending"
  const isLocked = status === "success" && submittedType !== null

  const doneActive = submittedType === "done" && status === "success"
  const stuckActive = submittedType === "stuck" && status === "success"

  
  const disableDone = isSending || (isLocked && submittedType !== "done")
  const disableStuck = isSending || (isLocked && submittedType !== "stuck")

  return (
    <div className="bg-warm-white text-blue rounded-[30px] px-5 py-8 md:px-9 md:py-10">
      <div
        className={cn(
          "flex flex-col md:flex-row gap-0 md:gap-9 pt-32 md:pt-0 relative",
          { "pt-56": tikTokQR }
        )}
      >
        <div className="absolute md:relative -top-16 left-0 right-0 md:inset-0 shrink-0">
          <div className="w-40 h-40 md:w-60 md:h-60 rounded-[30px] mx-auto bg-white">
            {platform?.content?.logo?.filename && (
              <Image
                src={platform?.content?.logo?.filename}
                height="240"
                width="240"
                alt={`${platform.name} logo`}
                className="w-40 h-40 md:h-60 md:w-60 object-contain  rounded-[30px]"
              />
            )}
          </div>
          {tikTokQR && (
              <Image
                src={tikTokQR?.filename}
                height="150"
                width="150"
                alt={tikTokQR.alt}
                className="w-32 h-32 md:h-56 md:w-56 mx-auto"
              />
            )}
        </div>

        <div className="grid grid-cols-1 gap-4 place-content-start grow">
          <div>
            <div className="eyebrow uppercase">
              {platform?.content?.category?.replace("-", " ")}
            </div>
            <h2>{platform.name}</h2>
          </div>

          {platform?.content?.access_info && (
            <div className="flex flex-wrap items-center gap-2 relative">
              <span className="uppercase font-bold tracking-wide text-bright-purple">
                GRANT ACCESS TO:
              </span>

              <span className="font-bold">{platform.content.access_info}</span>

              <button
                type="button"
                onClick={handleCopy}
                aria-label="Copy access info"
                className="relative text-blue hover:text-green-kelly transition-colors"
              >
                <CopyIcon className="w-4 h-4" />

                {copied && (
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-blue text-white text-xs px-2 py-1">
                    Copied!
                  </span>
                )}
              </button>
            </div>
          )}

          <ol className="list-decimal pl-6 flex flex-col gap-2">
            {platform?.content?.steps?.map((step) => (
              <li key={step._uid}>
                <span className="prose-p:inline">{render(step.text)}</span>

                {step?.screenshot?.filename && (
                  <button
                    type="button"
                    onClick={() => handleOpenScreenshot(step)}
                    className="xl:pl-2 underline hover:no-underline font-bold text-blue hover:text-green-kelly transition-colors align-middle inline"
                    title="View Screenshot"
                  >
                    <ScreenshotIcon className="w-5 h-5" />{" "}
                    <span className="sr-only">View Screenshot</span>
                  </button>
                )}
              </li>
            ))}
          </ol>
          <Modal open={open} onClose={handleCloseScreenshot}>
            <div className="p-12 flex flex-col items-center justify-center">
              {activeStep?.screenshot?.filename && (
                <Image
                  src={activeStep?.screenshot?.filename}
                  height="800"
                  width="1250"
                  alt={activeStep?.screenshot?.alt}
                />
              )}
              {activeStep?.text && <div>{render(activeStep.text)}</div>}
            </div>
          </Modal>

          <div className="flex flex-col lg:flex-row gap-4 self-end">
            <CallToAction
              button
              style="primary"
              onClick={() => notifyPlatform("done")}
              className={[
                disableDone ? "opacity-50 pointer-events-none" : "",
                doneActive ? "ring-2 ring-green-kelly" : "",
              ].join(" ")}
            >
              {isSending && submittedType === "done"
                ? "SENDING..."
                : doneActive
                ? "COMPLETED ✓"
                : "I'M DONE!"}
            </CallToAction>

            <CallToAction
              button
              style="ghost-dark"
              onClick={() => notifyPlatform("stuck")}
              className={[
                disableStuck ? "opacity-50 pointer-events-none" : "",
                stuckActive ? "ring-2 ring-green-kelly" : "",
              ].join(" ")}
            >
              {isSending && submittedType === "stuck"
                ? "SENDING..."
                : stuckActive
                ? "HELP REQUESTED ✓"
                : "I'M STUCK..."}
            </CallToAction>
          </div>

          {status === "success" && submittedType === "done" && (
            <p className="text-green-kelly text-sm font-bold mt-2">
              ✓ Marked as completed — Client Success has been notified.
            </p>
          )}

          {status === "success" && submittedType === "stuck" && (
            <div>
              <p className="text-green-kelly text-sm font-bold mt-2">
                ✓ Help requested — Client Success has been notified.
              </p>
              <button
                className="underline hover:no-underline"
                onClick={() => resetPlatform()}
              >
                Reset Status
              </button>
            </div>
          )}

          {status === "error" && (
            <p className="text-red-600 text-sm font-bold mt-2">
              Something went wrong sending the email. Please try again.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}



export default function PlatformsSection({ platforms, clientName, csmEmail, tikTokQR, pageUid }) {
  if (!Array.isArray(platforms) || platforms.length === 0) {
    return null;
  }

  return (
    <section id="get-started" className="bg-purple-to-dark-purple-gradient pt-72 pb-10 lg:py-20">
      <div className="container">
        <div className="flex flex-col gap-16 lg:gap-8">
          {platforms.filter(platform => platform.content?.steps?.length > 0).map((platform) => (
              <PlatformCard
                key={platform.uuid }
                platform={platform}
                clientName={clientName}
                csmEmail={csmEmail}
                tikTokQR={platform?.name === "TikTok" ? tikTokQR : undefined}
                pageUid={pageUid}
              />
          ))}
        </div>
      </div>
    </section>
  );
}
